import { Button } from '@/components/ui/button';
import EditableTitle from '@/components/ui/EditableTitle';
import TitleSkeleton from '@/components/ui/TitleSkeleton';
import { ChevronLeft, ChevronRight, Mail, MessageCircle, Phone, User } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { tramiteParametroService } from '../services/tramiteParametroService';
import { Persona } from '../types';
import { useAuthorization } from '@/auth/hooks/useAuthorization';

interface CardCompradorVendedorProps {
  data: Persona[];
  title: string;
  tramiteId?: number;
  parameterName?: string;
  onTitleChange?: (newTitle: string) => void;
  canSendWhatsAppQuick?: boolean;
  onSendWhatsAppQuick?: () => void;
}

const CardCompradorVendedor: React.FC<CardCompradorVendedorProps> = ({
  data,
  title,
  tramiteId,
  parameterName,
  onTitleChange,
  canSendWhatsAppQuick = false,
  onSendWhatsAppQuick,
}) => {
  const [index, setIndex] = useState(0);
  const [model, setModel] = useState<Persona | undefined>(data[0]);
  const [displayTitle, setDisplayTitle] = useState(title);
  const [loadingTitle, setLoadingTitle] = useState(false);
  const { canEditTitle } = useAuthorization();

  useEffect(() => {
    setModel(data[index]);
  }, [data, index]);

  // Load title from parameters if tramiteId and parameterName are provided
  useEffect(() => {
    const loadTitle = async () => {
      if (tramiteId && parameterName) {
        setLoadingTitle(true);
        try {
          const parameterTitle = await tramiteParametroService.getValueOrDefault(tramiteId, parameterName, title);
          setDisplayTitle(parameterTitle);
        } catch (error) {
          toast.error('Error al cargar el título del parámetro');
          setDisplayTitle(title);
        } finally {
          setLoadingTitle(false);
        }
      } else {
        setDisplayTitle(title);
      }
    };

    loadTitle();
  }, [tramiteId, parameterName, title]);

  const handleNext = () => {
    const newIndex = index + 1;
    if (data.length - 1 >= newIndex) {
      setIndex(newIndex);
    }
  };

  const handlePrev = () => {
    const newIndex = index - 1;
    if (newIndex >= 0 && newIndex <= data.length - 1) {
      setIndex(newIndex);
    }
  };

  const currentPerson = data[index];

  const handleTitleSave = async (newTitle: string) => {
    if (tramiteId && parameterName) {
      try {
        await tramiteParametroService.createOrUpdate(tramiteId, parameterName, newTitle, 'string');
        setDisplayTitle(newTitle);
      } catch (error) {
        toast.error('Error al guardar el título');
      }
    }

    if (onTitleChange) {
      onTitleChange(newTitle);
    }
  };

  return (
    <div className="flex flex-col justify-between items-center bg-white shadow-md rounded gap-3">
      <div className="text-base border-b border-red-300 w-full flex justify-between items-center gap-2 py-3 px-4">
        <div className="font-semibold flex min-w-0 items-center gap-2">
          <div className="min-w-0">
            {loadingTitle ? (
              <TitleSkeleton className="text-xs sm:text-sm" />
            ) : (
              <EditableTitle
                readOnly={!canEditTitle}
                title={displayTitle}
                onSave={handleTitleSave}
                className="inline-flex items-center"
                inputClassName="text-xs sm:text-sm font-semibold text-center"
                iconSize={12}
              />
            )}
          </div>
          {canSendWhatsAppQuick && onSendWhatsAppQuick && (
            <button
              type="button"
              className="h-7 w-7 shrink-0 rounded-full bg-green-600 text-white hover:bg-green-700 transition-colors flex items-center justify-center shadow-sm"
              aria-label={`Enviar WhatsApp rápido a ${displayTitle}`}
              title={`Enviar WhatsApp rápido a ${displayTitle}`}
              onClick={onSendWhatsAppQuick}
            >
              <MessageCircle size={14} />
            </button>
          )}
        </div>
        <span className='text-gray-500 ml-1 shrink-0'>{`(${index + 1}/${data.length})`}</span>
      </div>
      <div className="relative p-3 w-full h-full content-center">
        <div className="flex items-center justify-between">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handlePrev()}
            disabled={data.length <= 1}
            className="h-8 w-8 rounded-full hover:bg-gray-100 disabled:opacity-30 transition-all duration-200 flex-shrink-0"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>

          <div className="flex-1 mx-3 min-w-0">
            <div className="space-y-2">
              <div className="flex items-center gap-2 min-w-0">
                <div className="bg-blue-200 p-1 rounded-full flex-shrink-0">
                  <User className="h-3 w-3 text-gray-600" />
                </div>
                <span className="text-sm font-medium text-gray-800 truncate">
                  {currentPerson?.nombre || 'Sin nombre'}
                </span>
              </div>
              <div className="flex items-center gap-2 min-w-0">
                <div className="bg-green-200 p-1 rounded-full flex-shrink-0">
                  <Phone className="h-3 w-3 text-gray-600" />
                </div>
                <span className="text-sm text-gray-600 truncate">
                  {currentPerson?.telefono || 'Sin teléfono'}
                </span>
              </div>
              <div className="flex items-center gap-2 min-w-0">
                <div className="bg-orange-200 p-1 rounded-full flex-shrink-0">
                  <Mail className="h-3 w-3 text-gray-600" />
                </div>
                <span className="text-sm text-gray-600 truncate">
                  {currentPerson?.email || 'Sin email'}
                </span>
              </div>
            </div>
          </div>

          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleNext()}
            disabled={data.length <= 1}
            className="h-8 w-8 rounded-full hover:bg-gray-100 disabled:opacity-30 transition-all duration-200 flex-shrink-0"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>

        {data.length > 1 && (
          <div className="flex justify-center mt-3 space-x-1">
            {data.map((_, idx) => (
              <div
                key={idx}
                className={`h-2 w-2 rounded-full transition-all duration-200 ${idx === index ? 'bg-primary w-6' : 'bg-gray-300'
                  }`}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CardCompradorVendedor;
